import React, { FC, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
  Fade,
  Link,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BadgeIcon from '@mui/icons-material/Badge';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BRAND, BRAND_DARK, BRAND_DARKER, BG } from './healthWalletConstants';
import clearLogo from '../../assets/Clear_Logo_horizontal_navy.jpg';
import polygonLogo from '../../assets/logo.png';
import { useAuthProfile } from "../../app/hooks";
import type { ClearStatus } from '../../types/onboarding';
import { ClearHaloIcon } from './ClearHaloIcon';

interface OnboardingVerificationProps {
  onNext: () => void;
  onBack: () => void;
  phase?: 'processing' | 'success' | 'error' | null;
  clearStatus?: ClearStatus;
}

function getClearErrorMessage(clearStatus?: ClearStatus): React.ReactNode {
  const supportLink = <Link component={RouterLink} to="/contact">contact support</Link>;
  switch (clearStatus) {
    case 'expired':
      return 'Your verification session expired before it could be completed. Please try again.';
    case 'canceled':
      return 'Verification was canceled. Please try again when you\'re ready.';
    case 'awaiting_user_input':
      return 'Verification was not completed. Please try again.';
    case 'identity_mismatch':
      // NOTE: identity_mismatch cannot be triggered in the initial onboarding flow today —
      // clear_user_id is NULL on first verification so the PSUID check always passes.
      // This case only becomes reachable once CLEAR's user-matching API is integrated.
      return <>The identity provided does not match our records. Please {supportLink} for assistance.</>;
    case 'clear_id_conflict':
      return <>This identity is already associated with another account. Please {supportLink} for assistance.</>;
    case 'failed':
    default:
      return <>We were unable to verify your identity with the information provided. Please try again or {supportLink} to assist with your account.</>;
  }
}

type VerificationPhase = 'intro' | 'processing' | 'success' | 'error';

const CLEAR_STEPS = [
  { label: 'Scan ID Document', icon: <BadgeIcon /> },
  { label: 'Biometric Capture', icon: <CameraAltIcon /> },
  { label: 'Identity Match', icon: <FingerprintIcon /> },
];

export const OnboardingVerification: FC<OnboardingVerificationProps> = ({ onNext, onBack, phase: propsPhase, clearStatus }) => {
  const { profile: loggedInUserProfile } = useAuthProfile();
  // Phase is set by the parent via key={clearPhase} forcing a remount with propsPhase='success'|'error'.
  // Internal transitions (intro→processing) are local; parent never changes phase on a live instance.
  const [phase, setPhase] = useState<VerificationPhase>(propsPhase || 'intro');
  const [activeStep, setActiveStep] = useState(0);

  const isVerificationDisabled = phase === 'processing' || phase === 'success';

  const handleStartVerification = async () => {
    setPhase('processing');
    setActiveStep(0);
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/healthwallet/onboarding/verify';
      form.style.display = 'none';
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'id';
      idInput.value = loggedInUserProfile?.id || '';
      form.appendChild(idInput);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (err) {
      console.error('Verification error:', err);
      setPhase('error');
    }
  };

  const handleRetry = () => {
    setPhase('intro');
    setActiveStep(0);
  };

  return (
    <Box sx={{ bgcolor: BG, minHeight: '100svh', width: '100%', display: 'flex', flexDirection: 'column', mt: { xs: '-56px', sm: '-64px' } }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          pt: { xs: '88px', sm: '112px' },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }} aria-hidden="true">
              <FingerprintIcon sx={{ color: BRAND_DARKER, fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: '#8e8e93', letterSpacing: 1.5 }}>
                Step 2 of 4
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1c1c1e' }}>
                Identity Verification
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container component="main" maxWidth="sm" sx={{ mt: -2, pb: 6, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Logos — always visible */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Box component="img" src={clearLogo} alt="CLEAR" sx={{ height: 36, objectFit: 'contain' }} />
              <Typography sx={{ color: '#d1d5db', fontSize: '1.25rem', lineHeight: 1 }}>|</Typography>
              <Box component="img" src={polygonLogo} alt="Polygon" sx={{ height: 72, objectFit: 'contain' }} />
            </Box>
            {/* INTRO PHASE */}
            {(phase === 'intro' || phase === 'processing') && (
              <Fade in>
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1 }}>
                      {phase === 'intro' ? 'Verify Your Identity with CLEAR' : 'Processing CLEAR Verification...'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 400, mx: 'auto' }}>
                      {phase === 'intro'
                        ? 'To protect your health information, we partner with CLEAR to verify your identity before accessing your records.'
                        : 'Please wait while we verify your identity with CLEAR. This may take a few seconds.'
                      }
                    </Typography>
                  </Box>

                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-icon': { color: BRAND_DARKER } }}>
                    <Typography variant="body2">
                      {phase === 'intro'
                        ? (<><strong>Ready to verify your identity.</strong> Click the button below to start the secure CLEAR identity verification process. You'll be redirected to CLEAR's secure verification portal.</>)
                        : (<><strong>Verification in progress.</strong> Your identity verification is being processed by CLEAR. Please wait while we complete the secure verification process.</>)
                      }
                    </Typography>
                  </Alert>

                  <Paper elevation={0} sx={{ bgcolor: BG, borderRadius: 3, p: 3, mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: '#1c1c1e' }}>
                      What to expect:
                    </Typography>
                    {CLEAR_STEPS.map((step, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: i < CLEAR_STEPS.length - 1 ? 2 : 0 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND_DARKER, boxShadow: '0 2px 8px rgba(108,76,224,0.1)' }}>
                          {step.icon}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{step.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {i === 0 && 'Scan your driver\'s license or passport'}
                            {i === 1 && 'Quick selfie to match your ID photo'}
                            {i === 2 && 'Automated verification in seconds'}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                </Box>
              </Fade>
            )}

            {/* PROCESSING PHASE - Show progress bar */}
            {phase === 'processing' && (
              <Fade in>
                <Box role="status" aria-live="polite" aria-busy="true" sx={{ textAlign: 'center', py: 4 }}>
                  <>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#1c1c1e' }}>
                      Verifying Your Identity...
                    </Typography>

                    <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                      {CLEAR_STEPS.map((step, i) => (
                        <Step key={i} completed={activeStep > i}>
                          <StepLabel
                            StepIconProps={{
                              sx: {
                                '&.Mui-active': { color: BRAND_DARKER },
                                '&.Mui-completed': { color: '#16a34a' },
                              },
                            }}
                          >
                            <Typography variant="caption" fontWeight={activeStep === i ? 700 : 400}>
                              {step.label}
                            </Typography>
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>

                    <Box sx={{ px: 4, mb: 3 }}>
                      <LinearProgress
                        variant="determinate"
                        value={((activeStep + 1) / CLEAR_STEPS.length) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: BRAND_DARKER,
                        }
                        }
                      />
                    </Box>
                  </>
                </Box>
              </Fade>
            )}

            {/* SUCCESS PHASE */}
            {phase === 'success' && (
              <Fade in>
                <Box role="status" aria-live="polite" sx={{ textAlign: 'center', py: 1 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#f0fdf4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 48, color: '#16a34a' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
                    Identity Verified!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 350, mx: 'auto' }}>
                    Your identity has been successfully verified through CLEAR.
                  </Typography>
                  <Alert severity="success" sx={{ borderRadius: 2, textAlign: 'center', bgcolor: '#efeaff', color: 'text.primary', '& .MuiAlert-icon': { color: BRAND_DARKER } }}>
                    You can now proceed to set up your Health Wallet.
                  </Alert>
                </Box>
              </Fade>
            )}

            {/* ERROR PHASE */}
            {phase === 'error' && (
              <Fade in>
                <Box role="alert" aria-live="assertive" sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
                    Unable to verify your identity
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 350, mx: 'auto' }}>
                    {getClearErrorMessage(clearStatus)}
                  </Typography>
                </Box>
              </Fade>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: phase === 'intro' ? 'space-between' : 'center', p: { xs: 3, md: 4 }, pt: 0, gap: 2 }}>
            {phase === 'intro' && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{
                  color: BRAND_DARKER,
                  borderColor: '#d7d3f0',
                  borderRadius: 50,
                  textTransform: 'none',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.8rem', sm: '0.9375rem' },
                  '&:hover': { borderColor: BRAND_DARKER, bgcolor: '#f7f7fa' },
                }}
              >
                Back
              </Button>
            )}

            {phase === 'intro' && (
              <Button
                variant="contained"
                onClick={handleStartVerification}
                disabled={isVerificationDisabled}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#041A55',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '16px' },
                  whiteSpace: 'nowrap',
                  py: '0.75rem',
                  px: { xs: '1rem', sm: '1.5rem' },
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#2A3D75', boxShadow: 'none' },
                  '&:active': { bgcolor: '#1A294D', transform: 'scale(0.98)', boxShadow: 'none' },
                  '&:disabled': { opacity: 0.6, bgcolor: '#041A55', color: '#fff' },
                }}
              >
                <ClearHaloIcon />
                Verify with CLEAR
              </Button>
            )}

            {phase === 'success' && (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={onNext}
                sx={{
                  bgcolor: BRAND_DARKER,
                  borderRadius: 50,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  '&:hover': { bgcolor: '#5a3fc0' },
                }}
              >
                Continue
              </Button>
            )}

            {phase === 'error' && (
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#041A55',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '16px' },
                  whiteSpace: 'nowrap',
                  py: '0.75rem',
                  px: { xs: '1rem', sm: '1.5rem' },
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#2A3D75', boxShadow: 'none' },
                  '&:active': { bgcolor: '#1A294D', transform: 'scale(0.98)', boxShadow: 'none' },
                }}
              >
                <ClearHaloIcon />
                Verify with CLEAR
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
